import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';
import { CLASES, filtrar, resumen, remisionDe, comprobanteDe } from '../utils/papeles';
import Remision from './Remision';
import Comprobante from './Comprobante';

// El archivo: todo lo que se imprimió alguna vez.
//
// NO genera nada. Lista lo que ya está guardado para poder volver a sacar el
// papel — y usa los mismos componentes que lo imprimieron la primera vez, así
// que la copia sale idéntica al original. Una pantalla que volviera a *armar*
// las facturas sería una segunda forma de hacer lo mismo, y el día que las dos
// no coincidan nadie sabría a cuál creerle.
//
// El caso de verdad no es emitir: es REEMITIR. El cliente perdió la remisión,
// el contador pide copia, hay que ver cuánto se le pagó a alguien la quincena
// pasada.

const haceUnMes = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

function Papeles() {
  const [desde, setDesde] = useState(haceUnMes);
  const [hasta, setHasta] = useState(hoy);
  const [busca, setBusca] = useState('');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [remision, setRemision] = useState(null);
  const [comprobante, setComprobante] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/papeles', { params: { desde, hasta } })
      .then(r => { setDatos(r.data); setError(''); })
      .catch(() => setError('No se pudieron cargar. Recargue la página; si sigue igual, avise.'));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!datos) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const todos = datos.papeles || [];
  const lista = filtrar(todos, busca);
  const r = resumen(todos);

  const abrir = (p) => p.tipo === 'venta'
    ? setRemision(remisionDe(p, datos.lineas))
    : setComprobante(comprobanteDe(p));

  return (
    <>
      <Remision v={remision} alCerrar={() => setRemision(null)} />
      <Comprobante v={comprobante} alCerrar={() => setComprobante(null)} />

      <div className="encabezado">
        <h1>Papeles</h1>
        <p>Todo lo que se imprimió: remisiones y comprobantes, para volver a sacarlos</p>
      </div>

      <div className="bloque">
        <div className="cuerpo">
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Nombre, cédula o consecutivo (REM-0003)"
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px',
                     fontSize: 16, borderRadius: 8, border: '1px solid var(--linea)' }} />
          <div className="campos" style={{ marginTop: 12 }}>
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      {/* Las tres clases se cuentan aparte y NO se suman: una venta es plata
          que entra y un comprobante es plata que sale. Un total único de los
          tres no significaría nada. */}
      {todos.length > 0 && (
        <div className="rejilla">
          <div className="dato" style={{ '--color': 'var(--venta)' }}>
            <div className="rotulo">🧾 Remisiones de venta</div>
            <div className="valor cifra">{plata(r.venta.valor)}</div>
            <div className="pie">{r.venta.n} {r.venta.n === 1 ? 'papel' : 'papeles'}</div>
          </div>
          <div className="dato" style={{ '--color': 'var(--leche)' }}>
            <div className="rotulo">💵 Pagos de leche</div>
            <div className="valor cifra">{plata(r.finca.valor)}</div>
            <div className="pie">{r.finca.n} {r.finca.n === 1 ? 'papel' : 'papeles'}</div>
          </div>
          <div className="dato" style={{ '--color': 'var(--acento-vivo)' }}>
            <div className="rotulo">💵 Pagos de nómina</div>
            <div className="valor cifra">{plata(r.trabajador.valor)}</div>
            <div className="pie">
              {r.trabajador.n} {r.trabajador.n === 1 ? 'papel' : 'papeles'}
            </div>
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>
          🧾 Papeles
          <span className="contador">{lista.length}</span>
        </h2>

        {todos.length === 0 ? (
          <div className="vacio">
            No hay papeles en este periodo. Se van guardando solos: cada venta
            deja su remisión y cada pago su comprobante.
          </div>
        ) : lista.length === 0 ? (
          <div className="vacio">
            Ningún papel con eso. Pruebe con el nombre, la cédula o el número
            que trae escrito el papel.
          </div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>No.</th>
                  <th>Papel</th>
                  <th>A quién</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lista.map(p => {
                  const C = CLASES[p.tipo] || CLASES.venta;
                  return (
                    <tr key={`${p.tipo}-${p.id}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fecha(p.fecha)}</td>
                      {/* EL CONSECUTIVO, no el id de la base.

                          El id se comparte con las otras empresas del servidor
                          —por eso las ventas de Gabriel empezaron en 102— así
                          que pedirle «la 45» a alguien no servía de nada. El
                          consecutivo es de este negocio y empieza en 1.

                          Los papeles viejos, de antes de que existiera, se
                          numeraron por fecha; si a alguno le faltara, sigue
                          saliendo su id para no dejar la casilla vacía. */}
                      <td className="num" style={{ color: 'var(--tinta-tenue)',
                                                   whiteSpace: 'nowrap' }}>
                        {p.consecutivo || p.id}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {C.ico} {C.nombre}
                        {/* Una remisión fiada no es lo mismo que una pagada, y
                            en un archivo eso es lo primero que se busca. */}
                        {p.tipo === 'venta' && p.forma_pago === 'credito' && (
                          <span className="pinta ojo" style={{ marginLeft: 6 }}>Fiada</span>
                        )}
                      </td>
                      <td className="recio">
                        {p.quien}
                        {p.documento && (
                          <div style={{ fontSize: 12, fontWeight: 400,
                                        color: 'var(--tinta-tenue)' }}>
                            {p.documento}
                          </div>
                        )}
                      </td>
                      <td className="num recio">{plata(p.valor)}</td>
                      <td>
                        <button className="boton chico suave" onClick={() => abrir(p)}>
                          🖨️ Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="cuerpo" style={{ paddingTop: 10 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
            Esta pantalla <strong>no emite nada</strong>: vuelve a sacar el papel
            que ya se imprimió, con los mismos números con que se guardó. Para
            emitir uno nuevo se vende en 🛒 Vender o se paga en 🚜 Fincas y
            👷 Nómina.
          </p>
        </div>
      </div>
    </>
  );
}

export default Papeles;
